// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { canPlaceOrder, hasProductsWithInvalidPrice } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@werner/App/Components/Notify/Notify";
import { CustomAppState } from "@werner/App/Store/Reducers";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

type Props = OwnProps;

export interface CheckoutErrorMessagesStyles {
    wrapper?: InjectableCss;
}

export const checkoutErrorMessagesStyles: CheckoutErrorMessagesStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 12px;
            margin-top: 30px;
            width: 100%;
        `,
    },
};

const styles = checkoutErrorMessagesStyles;

const CheckoutErrorMessages: FC<Props> = ({ id }) => {
    const {
        cart,
        placeOrderErrorMessage,
        containsProductsWithInvalidPrice,
        canUpdateShipToAddress,
        showMessageOfPricesChangeforOTA,
    } = useAppSelector(state => {
        const { cartId } = state.pages.checkoutReviewAndSubmit;
        const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
        const billTo = getCurrentBillToState(state).value;
        const shipTo = getCurrentShipToState(state).value;
        const customerSettings = getSettingsCollection(state).customerSettings;
        return {
            cart: cartState.value,
            placeOrderErrorMessage: state.pages.checkoutReviewAndSubmit.placeOrderErrorMessage,
            containsProductsWithInvalidPrice: hasProductsWithInvalidPrice(cartState.value),
            canUpdateShipToAddress:
                billTo?.isGuest ||
                shipTo?.oneTimeAddress ||
                customerSettings.allowBillToAddressEdit ||
                customerSettings.allowShipToAddressEdit,
            showMessageOfPricesChangeforOTA: (state as CustomAppState).xCheckout.addresses
                .showMessageOfPricesChangeforOTA,
        };
    });

    return (
        <StyledWrapper {...styles.wrapper}>
            {showMessageOfPricesChangeforOTA && (
                <Notify type="warning">{siteMessage("Werner_Checkout_AdditionalFreightOnAddressChange")}</Notify>
            )}
            {placeOrderErrorMessage && <Notify type="danger">{placeOrderErrorMessage}</Notify>}
            {cart?.taxFailureReason === "InvalidAddress" && canUpdateShipToAddress && (
                <Notify type="danger">
                    {siteMessage("ReviewAndPay_InvalidAddress_CannotCalculateTax_UpdateAddress")}
                </Notify>
            )}
            {cart?.taxFailureReason === "InvalidAddress" && !canUpdateShipToAddress && (
                <Notify type="danger">{siteMessage("ReviewAndPay_InvalidAddress_CannotCalculateTax")}</Notify>
            )}
            {containsProductsWithInvalidPrice && (
                <Notify type="danger">{siteMessage("ReviewAndPay_HasProductsWithInvalidPrice")}</Notify>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CheckoutErrorMessages,
    definition: {
        group: "Xngage Custom" as any,
        allowedContexts: ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage"],
        displayName: "Checkout Error Messages",
        fieldDefinitions: [],
    },
};

export default widgetModule;
